/**
 * POSPropertySelector - Property Selection Page
 *
 * Shown when user has access to multiple properties.
 * Redirects directly if only one property.
 *
 * URL: pos.cleanmybed.com/
 */

import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import '../pos-theme.css';
import './pos-property-selector.css';

function POSPropertySelector() {
  const { user, signOut, supabase, getPortalAccess } = useAuth();
  const portalAccess = getPortalAccess();
  const navigate = useNavigate();

  const [properties, setProperties] = useState<Array<{ property_id: string; slug: string; property_name: string; group_name?: string; group_id?: string; access_level?: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function fetchProperties() {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        const { data, error: fetchError } = await supabase!
          .from('pos_user_properties')
          .select('*');

        if (fetchError) throw fetchError;

        if (!data || data.length === 0) {
          setError('No properties found. Please contact your administrator.');
          setLoading(false);
          return;
        }

        // If only one property, redirect directly
        if (data.length === 1) {
          navigate(`/pos/${data[0].slug}`, { replace: true });
          return;
        }

        setProperties(data);
        setLoading(false);
      } catch (_err: unknown) {
        setError('Failed to load properties. Please try again.');
        setLoading(false);
      }
    }

    fetchProperties();
  }, [user?.id, navigate, supabase]);

  if (loading) {
    return (
      <div className="pos-selector">
        <div className="pos-selector-content">
          <div className="pos-selector-loading">
            <div className="pos-selector-spinner" />
            <p>Loading your properties...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pos-selector">
        <div className="pos-selector-content">
          <div className="pos-selector-header">
            <h1 className="pos-selector-brand">PEACE OF SLEEP</h1>
          </div>
          <div className="pos-selector-card">
            <div className="pos-selector-error-icon">!</div>
            <h2>No Access</h2>
            <p>{error}</p>
            <div className="flex flex-col gap-sm mt-lg">
              {portalAccess.admin && (
                <Link
                  to="/"
                  className="pos-selector-btn pos-selector-btn-secondary"
                  style={{ textDecoration: 'none', textAlign: 'center' }}
                >
                  Operations Portal
                </Link>
              )}
              {portalAccess.ambassador && (
                <Link
                  to="/ambassador"
                  className="pos-selector-btn pos-selector-btn-secondary"
                  style={{ textDecoration: 'none', textAlign: 'center' }}
                >
                  Ambassador Portal
                </Link>
              )}
              <button
                className="pos-selector-btn-link"
                onClick={signOut}
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pos-selector">
      <div className="pos-selector-content">
        {/* Header */}
        <div className="pos-selector-header">
          <h1 className="pos-selector-brand">PEACE OF SLEEP</h1>
          <p className="pos-selector-tagline">The Quiet Standard Of Trust</p>
        </div>

        {/* Selection Card */}
        <div className="pos-selector-card">
          <h2>Select Property</h2>
          <p>Choose a property to manage.</p>

          <div className="pos-selector-properties">
            {(() => {
              // Group properties by group_name for grouped display
              const grouped = new Map<string, typeof properties>();
              const ungrouped: typeof properties = [];
              for (const p of properties) {
                if (p.group_name) {
                  const list = grouped.get(p.group_name) || [];
                  list.push(p);
                  grouped.set(p.group_name, list);
                } else {
                  ungrouped.push(p);
                }
              }

              const hasGroups = grouped.size > 0;
              const sections: JSX.Element[] = [];

              // Render grouped properties with customer headings
              for (const [groupName, groupProperties] of grouped) {
                const customerId = groupProperties[0]?.group_id;
                sections.push(
                  <div key={groupName} className="pos-selector-group">
                    <div className="pos-selector-group-header">
                      <span className="pos-selector-group-name">{groupName}</span>
                      {customerId && groupProperties.length > 1 && (
                        <button
                          className="pos-selector-group-dashboard-link"
                          onClick={() => navigate(`/pos/group/${encodeURIComponent(groupName.toLowerCase().replace(/\s+/g, '-'))}`)}
                        >
                          Group Dashboard
                        </button>
                      )}
                    </div>
                    {groupProperties.map(property => (
                      <button
                        key={property.property_id}
                        className="pos-selector-property"
                        onClick={() => navigate(`/pos/${property.slug}`)}
                      >
                        <div className="pos-selector-property-info">
                          <span className="pos-selector-property-name">
                            {property.property_name}
                          </span>
                        </div>
                        <span className="pos-selector-property-access">
                          {property.access_level}
                        </span>
                      </button>
                    ))}
                  </div>
                );
              }

              // Render ungrouped properties
              for (const property of ungrouped) {
                sections.push(
                  <button
                    key={property.property_id}
                    className="pos-selector-property"
                    onClick={() => navigate(`/pos/${property.slug}`)}
                  >
                    <div className="pos-selector-property-info">
                      <span className="pos-selector-property-name">
                        {property.property_name}
                      </span>
                      {hasGroups && property.group_name && (
                        <span className="pos-selector-property-customer">
                          {property.group_name}
                        </span>
                      )}
                    </div>
                    <span className="pos-selector-property-access">
                      {property.access_level}
                    </span>
                  </button>
                );
              }

              return sections;
            })()}
          </div>

          <div className="pos-selector-footer">
            <button
              className="pos-selector-btn-link"
              onClick={signOut}
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default POSPropertySelector;
